/**
 * DocumentUploadSection
 * v3.1 - ITEM-43: Navigate to /dashboard immediately on upload success - 2026-07-14
 * v3.0 - UNSTRUCTURED-01 Phase 4: page counter wired from GET /list, 402 exhaustion Alert,
 *         top-up amber notice (auto-dismiss 10s) - 2026-04-20
 * v2.0 - UNSTRUCTURED-01 Phase 2: OneDrive/Google Drive pickers, dismissible stale banner,
 *         analytics bridge banner - 2026-04-19
 * v1.0 - UNSTRUCTURED-01 Phase 1: Drag-and-drop document upload with status chips - 2026-04-19
 *
 * Features (v2.0 additions):
 * - OneDrive picker via window.OneDrive.open() (OneDrive.js v7.2 loaded in index.html)
 * - Google Drive picker via gapi.load("picker") (apis.google.com/js/api.js loaded in index.html)
 * - Stale banner is dismissible (sessionStorage key, resets on new bronze_ready session)
 * - Analytics bridge banner: when documents are bronze_ready, show "View Analytics" nudge
 * - stale_reminder_days from backend (replaces hardcoded 72h)
 *
 * Env vars required (baked into build):
 *   REACT_APP_ONEDRIVE_CLIENT_ID   — Azure AD / Live app registration client_id
 *   REACT_APP_GOOGLE_CLIENT_ID     — Google OAuth 2.0 client_id
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Analytics,
  CheckCircle,
  Close,
  CloudUpload,
  Description,
  Error as ErrorIcon,
  History,
  HourglassEmpty,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  ingestService,
  DocumentListItem,
  DocumentStatusResponse,
} from '../services/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.csv', '.txt', '.png', '.jpg', '.jpeg'];
const MAX_FILE_SIZE_MB = 50;
const POLL_INTERVAL_MS = 5000;
const IN_FLIGHT_STATUSES = new Set(['queued', 'processing']);
const STALE_DISMISS_KEY = 'unstructured_stale_banner_dismissed';

const ONEDRIVE_CLIENT_ID = process.env.REACT_APP_ONEDRIVE_CLIENT_ID || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface UploadEntry {
  key: string;
  filename: string;
  status: 'uploading' | 'queued' | 'processing' | 'bronze_ready' | 'failed' | 'error';
  sessionId?: string;
  errorMessage?: string;
  pageCountEstimated?: number;
  pageCountActual?: number;
  elementCount?: number;
  createdAt?: string;
  uploadSource?: string;
}

interface PageUsageSummary {
  allowance_pages: number;
  pages_used: number;
  pages_reserved: number;
  topup_pages_remaining: number;
  pages_remaining: number;
  period_month: string;
}

// Extend window for picker SDKs loaded in index.html
declare global {
  interface Window {
    OneDrive?: {
      open: (options: Record<string, unknown>) => void;
    };
    gapi?: {
      load: (lib: string, callback: () => void) => void;
      client?: { init: (opts: Record<string, unknown>) => Promise<void> };
      auth2?: { getAuthInstance: () => { currentUser: { get: () => { getAuthResponse: () => { access_token: string } } } } };
      picker?: {
        api: {
          PickerBuilder: new () => {
            addView: (view: unknown) => unknown;
            setOAuthToken: (token: string) => unknown;
            setDeveloperKey: (key: string) => unknown;
            setCallback: (cb: (data: unknown) => void) => unknown;
            build: () => { setVisible: (v: boolean) => void };
          };
          DocsView: new () => unknown;
          Action: { PICKED: string };
        };
      };
    };
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (resp: { access_token?: string; error?: string }) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
  }
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------
function StatusChip({ status, errorMessage }: { status: UploadEntry['status']; errorMessage?: string }) {
  switch (status) {
    case 'uploading':
      return <Chip icon={<CircularProgress size={12} />} label="Uploading" size="small" color="default" variant="outlined" />;
    case 'queued':
      return <Chip icon={<HourglassEmpty fontSize="small" />} label="Queued" size="small" color="info" variant="outlined" />;
    case 'processing':
      return <Chip icon={<CircularProgress size={12} />} label="Processing" size="small" color="warning" variant="outlined" />;
    case 'bronze_ready':
      return <Chip icon={<CheckCircle fontSize="small" />} label="Ready" size="small" color="success" />;
    case 'failed':
    case 'error': {
      const label = errorMessage ? `Failed: ${errorMessage.substring(0, 40)}` : 'Failed';
      return (
        <Tooltip title={errorMessage || 'Processing failed'}>
          <Chip icon={<ErrorIcon fontSize="small" />} label={label} size="small" color="error" />
        </Tooltip>
      );
    }
    default:
      return <Chip label={status} size="small" />;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function DocumentUploadSection() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [pageUsage, setPageUsage] = useState<PageUsageSummary | null>(null);
  const [staleWarning, setStaleWarning] = useState(false);
  const [staleReminderDays, setStaleReminderDays] = useState(3);
  const [staleDismissed, setStaleDismissed] = useState(
    () => sessionStorage.getItem(STALE_DISMISS_KEY) === '1'
  );
  const [dragActive, setDragActive] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  // v3.0 — B2: 402 page exhaustion inline alert
  const [exhaustionError, setExhaustionError] = useState<string | null>(null);
  // v3.0 — B3: top-up amber notice (auto-dismiss 10s)
  const [topupNotice, setTopupNotice] = useState(false);
  const topupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Has any bronze_ready document been completed in this session?
  const hasReady = entries.some(e => e.status === 'bronze_ready');

  // v3.0 — B3: cleanup topup timer on unmount
  useEffect(() => {
    return () => { if (topupTimerRef.current) clearTimeout(topupTimerRef.current); };
  }, []);

  // v3.0 — B3: show top-up amber notice and auto-dismiss after 10s
  function showTopupNotice() {
    setTopupNotice(true);
    if (topupTimerRef.current) clearTimeout(topupTimerRef.current);
    topupTimerRef.current = setTimeout(() => setTopupNotice(false), 10000);
  }

  // ---------------------------------------------------------------------------
  // Load existing sessions on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadDocumentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadDocumentList() {
    try {
      setLoadingList(true);
      const data = await ingestService.listDocuments(undefined, 50, 0);
      const loaded: UploadEntry[] = data.documents.map((doc: DocumentListItem) => ({
        key: doc.session_id,
        filename: doc.filename,
        status: doc.status as UploadEntry['status'],
        sessionId: doc.session_id,
        errorMessage: doc.error_message || undefined,
        pageCountEstimated: doc.page_count_estimated,
        pageCountActual: doc.page_count_actual,
        elementCount: doc.element_count,
        createdAt: doc.created_at,
      }));
      setEntries(loaded);
      if (data.page_usage) setPageUsage(data.page_usage);
      setStaleWarning(data.stale_warning);
      if ((data as any).stale_reminder_days) {
        setStaleReminderDays((data as any).stale_reminder_days);
      }
    } catch {
      // Non-fatal
    } finally {
      setLoadingList(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Polling for in-flight sessions
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const inFlight = entries.filter(e => IN_FLIGHT_STATUSES.has(e.status) && e.sessionId);
    if (inFlight.length === 0) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (!pollRef.current) {
      pollRef.current = setInterval(async () => {
        const current = entries.filter(e => IN_FLIGHT_STATUSES.has(e.status) && e.sessionId);
        if (current.length === 0) { clearInterval(pollRef.current!); pollRef.current = null; return; }
        await Promise.all(current.map(e => pollSession(e.sessionId!)));
      }, POLL_INTERVAL_MS);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  async function pollSession(sessionId: string) {
    try {
      const status: DocumentStatusResponse = await ingestService.getDocumentStatus(sessionId);
      const nowBronzeReady = status.status === 'bronze_ready';
      setEntries(prev =>
        prev.map(e =>
          e.sessionId === sessionId
            ? {
                ...e,
                status: status.status as UploadEntry['status'],
                errorMessage: status.error_message || undefined,
                pageCountActual: status.page_count_actual,
                elementCount: status.element_count,
              }
            : e
        )
      );
      // When a session becomes bronze_ready, reset the stale dismiss so the analytics bridge shows
      if (nowBronzeReady) setStaleDismissed(false);
    } catch {
      // Ignore poll errors
    }
  }

  // ---------------------------------------------------------------------------
  // Stale banner dismiss
  // ---------------------------------------------------------------------------
  function dismissStaleBanner() {
    sessionStorage.setItem(STALE_DISMISS_KEY, '1');
    setStaleDismissed(true);
  }

  // ---------------------------------------------------------------------------
  // Client-side validation
  // ---------------------------------------------------------------------------
  function validateFile(file: File): string | null {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) return `File type ${ext} is not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) return `File exceeds ${MAX_FILE_SIZE_MB} MB limit.`;
    if (file.size === 0) return 'File is empty.';
    return null;
  }

  // ---------------------------------------------------------------------------
  // Upload handler (drag-and-drop / browse)
  // ---------------------------------------------------------------------------
  async function handleFiles(files: FileList | File[]) {
    for (const file of Array.from(files)) {
      const validationError = validateFile(file);
      const tempKey = `tmp-${Date.now()}-${Math.random()}`;
      if (validationError) {
        setEntries(prev => [{ key: tempKey, filename: file.name, status: 'error', errorMessage: validationError }, ...prev]);
        continue;
      }
      setEntries(prev => [{ key: tempKey, filename: file.name, status: 'uploading' }, ...prev]);
      try {
        const response = await ingestService.uploadDocument(file);
        setEntries(prev =>
          prev.map(e =>
            e.key === tempKey
              ? { key: response.session_id, filename: response.filename, status: response.status as UploadEntry['status'], sessionId: response.session_id, pageCountEstimated: response.page_count_estimated }
              : e
          )
        );
        // v3.0 — B3: show amber notice when upload draws from top-up pool
        if (response.topup_notice) showTopupNotice();
        setExhaustionError(null);
        // v3.1 — ITEM-43: go to dashboard immediately so the processing banner is visible
        navigate('/dashboard');
      } catch (err: any) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Upload failed';
        if (status === 402) {
          // v3.0 — B2: 402 exhaustion — show inline alert, clear the uploading entry
          setExhaustionError(msg);
          setEntries(prev => prev.filter(e => e.key !== tempKey));
        } else {
          setEntries(prev => prev.map(e => e.key === tempKey ? { ...e, status: 'error', errorMessage: msg } : e));
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // OneDrive picker (v2.0)
  // ---------------------------------------------------------------------------
  function openOneDrivePicker() {
    if (!ONEDRIVE_CLIENT_ID) {
      setSnackbar({ open: true, message: 'OneDrive is not configured for this environment.', severity: 'info' });
      return;
    }
    if (!window.OneDrive) {
      setSnackbar({ open: true, message: 'OneDrive picker is loading — please try again in a moment.', severity: 'info' });
      return;
    }
    window.OneDrive.open({
      clientId: ONEDRIVE_CLIENT_ID,
      action: 'download',
      multiSelect: false,
      advanced: { filter: ALLOWED_EXTENSIONS.join(',') },
      success: async (files: any) => {
        const file = files?.value?.[0];
        if (!file) return;
        const downloadUrl: string = file['@microsoft.graph.downloadUrl'] || '';
        const filename: string = file.name || 'onedrive-file';
        const fileSizeBytes: number = file.size || 0;
        if (!downloadUrl) {
          setSnackbar({ open: true, message: 'OneDrive did not return a download URL.', severity: 'error' });
          return;
        }
        const tempKey = `onedrive-${Date.now()}`;
        setEntries(prev => [{ key: tempKey, filename, status: 'uploading', uploadSource: 'onedrive' }, ...prev]);
        try {
          const response = await ingestService.uploadFromOneDrive({ download_url: downloadUrl, filename, file_size_bytes: fileSizeBytes });
          setEntries(prev =>
            prev.map(e =>
              e.key === tempKey
                ? { key: response.session_id, filename: response.filename, status: response.status as UploadEntry['status'], sessionId: response.session_id, pageCountEstimated: response.page_count_estimated, uploadSource: 'onedrive' }
                : e
            )
          );
          if (response.topup_notice) showTopupNotice();
          setExhaustionError(null);
          // v3.1 — ITEM-43: go to dashboard immediately so the processing banner is visible
          navigate('/dashboard');
        } catch (err: any) {
          const status = err?.response?.status;
          const msg = err?.response?.data?.detail || err?.message || 'OneDrive upload failed';
          if (status === 402) {
            setExhaustionError(msg);
            setEntries(prev => prev.filter(e => e.key !== tempKey));
          } else {
            setEntries(prev => prev.map(e => e.key === tempKey ? { ...e, status: 'error', errorMessage: msg } : e));
          }
        }
      },
      error: (err: any) => {
        setSnackbar({ open: true, message: `OneDrive picker error: ${err?.message || 'Unknown error'}`, severity: 'error' });
      },
      cancel: () => {},
    });
  }

  // ---------------------------------------------------------------------------
  // Google Drive picker (v2.0)
  // ---------------------------------------------------------------------------
  function openGoogleDrivePicker() {
    if (!GOOGLE_CLIENT_ID) {
      setSnackbar({ open: true, message: 'Google Drive is not configured for this environment.', severity: 'info' });
      return;
    }
    if (!window.gapi) {
      setSnackbar({ open: true, message: 'Google Picker is loading — please try again in a moment.', severity: 'info' });
      return;
    }

    // Request an OAuth access token via GIS, then open the picker
    const tokenClient = window.google?.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/drive.readonly',
      callback: (tokenResponse: { access_token?: string; error?: string }) => {
        if (tokenResponse.error || !tokenResponse.access_token) {
          setSnackbar({ open: true, message: `Google auth failed: ${tokenResponse.error || 'no token'}`, severity: 'error' });
          return;
        }
        const accessToken = tokenResponse.access_token;
        window.gapi!.load('picker', () => {
          if (!window.gapi?.picker) {
            setSnackbar({ open: true, message: 'Google Picker failed to load.', severity: 'error' });
            return;
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const gapiAny = window.gapi as any;
          const picker = new gapiAny.picker.api.PickerBuilder()
            .addView(new gapiAny.picker.api.DocsView())
            .setOAuthToken(accessToken)
            .setCallback(async (data: any) => {
              if (data.action !== gapiAny.picker?.api.Action.PICKED) return;
              const doc = data.docs?.[0];
              if (!doc) return;
              const fileId: string = doc.id;
              const filename: string = doc.name || 'googledrive-file';
              const fileSizeBytes: number = doc.sizeBytes || 0;
              const tempKey = `gdrive-${Date.now()}`;
              setEntries(prev => [{ key: tempKey, filename, status: 'uploading', uploadSource: 'googledrive' }, ...prev]);
              try {
                const response = await ingestService.uploadFromGoogleDrive({ file_id: fileId, access_token: accessToken, filename, file_size_bytes: fileSizeBytes });
                setEntries(prev =>
                  prev.map(e =>
                    e.key === tempKey
                      ? { key: response.session_id, filename: response.filename, status: response.status as UploadEntry['status'], sessionId: response.session_id, pageCountEstimated: response.page_count_estimated, uploadSource: 'googledrive' }
                      : e
                  )
                );
                if (response.topup_notice) showTopupNotice();
                setExhaustionError(null);
                // v3.1 — ITEM-43: go to dashboard immediately so the processing banner is visible
                navigate('/dashboard');
              } catch (err: any) {
                const gdStatus = err?.response?.status;
                const msg = err?.response?.data?.detail || err?.message || 'Google Drive upload failed';
                if (gdStatus === 402) {
                  setExhaustionError(msg);
                  setEntries(prev => prev.filter(e => e.key !== tempKey));
                } else {
                  setEntries(prev => prev.map(e => e.key === tempKey ? { ...e, status: 'error', errorMessage: msg } : e));
                }
              }
            })
            .build();
          picker.setVisible(true);
        });
      },
    });
    tokenClient?.requestAccessToken();
  }

  // ---------------------------------------------------------------------------
  // Drag-and-drop
  // ---------------------------------------------------------------------------
  const handleDragEnter = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleFiles(e.dataTransfer.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const inFlightCount = entries.filter(e => IN_FLIGHT_STATUSES.has(e.status)).length;
  const showStaleBanner = staleWarning && !staleDismissed;

  return (
    <Box sx={{ mt: 4 }}>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Description sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Document Upload</Typography>
        <Tooltip title="View history">
          <IconButton size="small" onClick={() => setHistoryOpen(true)}><History fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Refresh list">
          <IconButton size="small" onClick={loadDocumentList} disabled={loadingList}><Refresh fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {/* v2.0 — Stale processing banner (dismissible) */}
      {showStaleBanner && (
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" color="inherit" onClick={dismissStaleBanner}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          Some documents were processed more than {staleReminderDays} day{staleReminderDays !== 1 ? 's' : ''} ago.
          Your connected data sources may have newer information.
        </Alert>
      )}

      {/* v3.0 — B2: 402 page exhaustion alert — cleared on next successful upload */}
      {exhaustionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" color="inherit" onClick={() => setExhaustionError(null)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {exhaustionError}
        </Alert>
      )}

      {/* v3.0 — B3: top-up amber notice — auto-dismisses after 10s */}
      {topupNotice && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" color="inherit" onClick={() => setTopupNotice(false)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          This upload is drawing from your top-up page pool. Contact support when your top-up runs low.
        </Alert>
      )}

      {/* v2.0 — Analytics bridge banner: nudge to Analytics when documents are ready */}
      {hasReady && (
        <Alert
          severity="success"
          icon={<Analytics />}
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<Analytics />}
              onClick={() => navigate('/dashboard')}
            >
              View Analytics
            </Button>
          }
        >
          Documents processed — new insights are available in your analytics dashboard.
        </Alert>
      )}

      {/* Drag-and-drop zone */}
      <Paper
        variant="outlined"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          backgroundColor: dragActive ? 'action.hover' : 'background.paper',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.2s, background-color 0.2s',
          '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          style={{ display: 'none' }}
          onChange={e => e.target.files && handleFiles(e.target.files)}
        />
        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" fontWeight={500}>Drag and drop files here, or click to browse</Typography>
        <Typography variant="caption" color="text.secondary">
          Supported: {ALLOWED_EXTENSIONS.join(', ')} · Max {MAX_FILE_SIZE_MB} MB per file
        </Typography>
      </Paper>

      {/* v2.0 — Cloud storage buttons (enabled) */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Tooltip title={ONEDRIVE_CLIENT_ID ? 'Upload from OneDrive' : 'OneDrive client ID not configured'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); openOneDrivePicker(); }}
            >
              OneDrive
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={GOOGLE_CLIENT_ID ? 'Upload from Google Drive' : 'Google Drive client ID not configured'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); openGoogleDrivePicker(); }}
            >
              Google Drive
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Monthly page usage bar */}
      {pageUsage && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Pages used this month</Typography>
            <Typography variant="caption" color="text.secondary">
              {pageUsage.pages_used} / {pageUsage.allowance_pages + pageUsage.topup_pages_remaining}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (pageUsage.pages_used / Math.max(1, pageUsage.allowance_pages + pageUsage.topup_pages_remaining)) * 100)}
            sx={{ borderRadius: 1, height: 6 }}
          />
          <Typography variant="caption" color="text.secondary">
            {pageUsage.pages_remaining} pages remaining · {pageUsage.period_month}
          </Typography>
        </Box>
      )}

      {/* Document list */}
      {loadingList ? (
        <Box sx={{ mt: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : entries.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          {inFlightCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {inFlightCount} document{inFlightCount > 1 ? 's' : ''} processing · auto-refreshing
            </Typography>
          )}
          <List dense disablePadding>
            {entries.map(entry => (
              <ListItem
                key={entry.key}
                disableGutters
                sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Description fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>{entry.filename}</Typography>
                      {entry.uploadSource && entry.uploadSource !== 'portal' && (
                        <Chip label={entry.uploadSource} size="small" variant="outlined" sx={{ fontSize: 10, height: 16 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    entry.pageCountActual != null
                      ? `${entry.pageCountActual} pages · ${entry.elementCount ?? 0} elements extracted`
                      : entry.pageCountEstimated != null
                      ? `~${entry.pageCountEstimated} pages (estimated)`
                      : undefined
                  }
                />
                <StatusChip status={entry.status} errorMessage={entry.errorMessage} />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          No documents uploaded yet. Drop a file above to get started.
        </Typography>
      )}

      {/* Snackbar for picker errors/info */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />

      {/* History modal stub */}
      {historyOpen && (
        <Box
          sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setHistoryOpen(false)}
        >
          <Paper sx={{ p: 4, maxWidth: 480, width: '90%' }} onClick={e => e.stopPropagation()}>
            <Typography variant="h6" mb={2}>Document History</Typography>
            <Typography variant="body2" color="text.secondary">
              Full-text search over extracted document content is on the roadmap.
            </Typography>
            <Button sx={{ mt: 2 }} onClick={() => setHistoryOpen(false)} variant="contained" size="small">Close</Button>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
